import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  Science,
  Analytics,
  CheckCircle,
  Assignment,
  Timer,
  PriorityHigh,
  TrendingUp,
  Assessment,
  Refresh,
  Save,
  Add,
  Opacity,
  LocalDrink,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';

interface VolumetrieAnalysisMethod {
  method: 'ACID_BASE' | 'REDOX' | 'COMPLEXOMETRIC' | 'PRECIPITATION';
  name: string;
  indicator: string;
  titrant: string;
  detection_limit: number;
  typical_range: string;
  description: string;
}

interface VolumetrieAnalysisResult {
  analyte: string;
  method: string;
  initial_volume: number;
  final_volume: number;
  volume_consumed: number;
  concentration_mol_l: number;
  concentration_percent: number;
  rsd_percent: number;
  titration_replicates: number;
  quality_flag: 'PASS' | 'WARNING' | 'FAIL';
  endpoint_detection: 'VISUAL' | 'POTENTIOMETRIC' | 'CONDUCTOMETRIC';
}

interface VolumetrieInstrumentConditions {
  burette_volume: number;
  burette_precision: number;
  temperature: number;
  ph_conditions?: number;
  stirring_speed?: number;
  titration_speed: 'FAST' | 'NORMAL' | 'SLOW';
  endpoint_criteria: string;
}

interface VolumetrieAnalysisData {
  sample_id: string;
  preparation_data: any;
  instrument_conditions: VolumetrieInstrumentConditions;
  calibration_standards: any[];
  quality_controls: any[];
  analysis_results: VolumetrieAnalysisResult[];
  analysis_completed_at?: string;
  analyst_id?: string;
  analyst_name?: string;
  notes: string;
  data_import_method: 'MANUAL' | 'CSV' | 'EXCEL' | 'VOLUMETRIC_FORM';
}

const VolumetrieAnalystDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [analysisDialog, setAnalysisDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [analysisData, setAnalysisData] = useState<VolumetrieAnalysisData>({
    sample_id: '',
    preparation_data: null,
    instrument_conditions: {
      burette_volume: 50,
      burette_precision: 0.1,
      temperature: 25,
      ph_conditions: 7.0,
      stirring_speed: 300,
      titration_speed: 'NORMAL',
      endpoint_criteria: 'Visual color change',
    },
    calibration_standards: [],
    quality_controls: [],
    analysis_results: [],
    notes: '',
    data_import_method: 'VOLUMETRIC_FORM',
  });

  const [stats, setStats] = useState({
    ready_for_analysis: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
    total_titrations: 0,
    average_precision: 0,
  });

  const volumetrieMethods: VolumetrieAnalysisMethod[] = [
    {
      method: 'ACID_BASE',
      name: 'Titrage Acide-Base',
      indicator: 'Phénolphtaléine',
      titrant: 'NaOH 0.1M',
      detection_limit: 0.001,
      typical_range: '0.001 - 10 %',
      description: 'Titrage pour déterminer l\'acidité ou la basicité'
    },
    {
      method: 'REDOX',
      name: 'Titrage Redox',
      indicator: 'Diphenylamine',
      titrant: 'KMnO4 0.02M',
      detection_limit: 0.005,
      typical_range: '0.005 - 5 %',
      description: 'Titrage d\'oxydoréduction pour métaux'
    },
    {
      method: 'COMPLEXOMETRIC',
      name: 'Titrage Complexométrique',
      indicator: 'NET (Noir Eriochrome T)',
      titrant: 'EDTA 0.01M',
      detection_limit: 0.01,
      typical_range: '0.01 - 20 %',
      description: 'Titrage EDTA pour Ca²⁺, Mg²⁺, métaux divalents'
    },
    {
      method: 'PRECIPITATION',
      name: 'Titrage par Précipitation',
      indicator: 'Chromate de potassium',
      titrant: 'AgNO3 0.1M',
      detection_limit: 0.01,
      typical_range: '0.01 - 10 %',
      description: 'Méthode de Mohr pour chlorures'
    }
  ];

  useEffect(() => {
    loadVolumetrieAnalysisData();
    const interval = setInterval(loadVolumetrieAnalysisData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadVolumetrieAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      
      const allSamples = await samplesService.getSamples();
      const volumetrieSamples = allSamples.filter((sample: any) => 
        sample.statut === 'LABORATOIRE_VOLUMETRIE_ANALYSE' || 
        sample.current_laboratory === 'LABORATOIRE_VOLUMETRIE' ||
        sample.selected_laboratory_methods?.some((method: string) => method.includes('VOLUMETRIE'))
      );
      
      setSamples(volumetrieSamples);
      
      const readyForAnalysis = volumetrieSamples.filter((s: any) => s.statut === 'LABORATOIRE_VOLUMETRIE_ANALYSE').length;
      const inProgress = volumetrieSamples.filter((s: any) => s.statut === 'LABORATOIRE_VOLUMETRIE_ANALYSE' && s.progress_percentage && s.progress_percentage > 0 && s.progress_percentage < 100).length;
      const completedToday = volumetrieSamples.filter((s: any) => {
        if (!s.updated_at) return false;
        const today = new Date().toISOString().split('T')[0];
        return s.updated_at.startsWith(today) && s.statut === 'VALIDATION_SERVICE';
      }).length;
      const urgentSamples = volumetrieSamples.filter((s: any) => s.priority === 'URGENT' || s.priorite === 'URGENT').length;
      
      setStats({
        ready_for_analysis: readyForAnalysis,
        in_progress: inProgress,
        completed_today: completedToday,
        urgent_samples: urgentSamples,
        total_titrations: completedToday * 3,
        average_precision: 98.5,
      });

    } catch (error) {
      console.error('❌ Error loading Volumétrie analysis data:', error);
      setError('Erreur lors du chargement des données d\'analyse Volumétrie');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAnalysis = (sample: Sample) => {
    setSelectedSample(sample);
    setAnalysisData(prev => ({
      ...prev,
      sample_id: sample.sample_id,
      preparation_data: sample.metadonnees?.preparation_data || null,
    }));
    setAnalysisDialog(true);
  };

  const handleCompleteAnalysis = async () => {
    if (!selectedSample) return;

    try {
      const updatedSample = {
        ...selectedSample,
        statut: 'VALIDATION_SERVICE' as SampleStatus,
        progress_percentage: 100,
        metadonnees: {
          ...selectedSample.metadonnees,
          volumetrie_analysis_form: {
            ...analysisData,
            analysis_completed_at: new Date().toISOString(),
            analyst_id: user?.id,
            analyst_name: `${user?.firstName} ${user?.lastName}`,
          }
        }
      };

      await samplesService.updateSample(selectedSample.id, updatedSample);
      
      loadVolumetrieAnalysisData();
      setAnalysisDialog(false);
      setSelectedSample(null);
      
      setAnalysisData({
        sample_id: '',
        preparation_data: null,
        instrument_conditions: {
          burette_volume: 50,
          burette_precision: 0.1,
          temperature: 25,
          ph_conditions: 7.0,
          stirring_speed: 300,
          titration_speed: 'NORMAL',
          endpoint_criteria: 'Visual color change',
        },
        calibration_standards: [],
        quality_controls: [],
        analysis_results: [],
        notes: '',
        data_import_method: 'VOLUMETRIC_FORM',
      });

    } catch (error) {
      console.error('Error completing Volumétrie analysis:', error);
      setError('Erreur lors de la finalisation de l\'analyse Volumétrie');
    }
  };

  const addAnalysisResult = () => {
    const newResult: VolumetrieAnalysisResult = {
      analyte: '',
      method: 'ACID_BASE',
      initial_volume: 0,
      final_volume: 0,
      volume_consumed: 0,
      concentration_mol_l: 0,
      concentration_percent: 0,
      rsd_percent: 0,
      titration_replicates: 3,
      quality_flag: 'PASS',
      endpoint_detection: 'VISUAL',
    };

    setAnalysisData(prev => ({
      ...prev,
      analysis_results: [...prev.analysis_results, newResult]
    }));
  };

  const updateAnalysisResult = (index: number, field: keyof VolumetrieAnalysisResult, value: any) => {
    setAnalysisData(prev => ({
      ...prev,
      analysis_results: prev.analysis_results.map((result, i) => {
        if (i === index) {
          const updated = { ...result, [field]: value };
          
          if (field === 'initial_volume' || field === 'final_volume') {
            updated.volume_consumed = updated.final_volume - updated.initial_volume;
          }
          
          return updated;
        }
        return result;
      })
    }));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'NORMAL': return 'info';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données Volumétrie...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalDrink sx={{ color: '#059669' }} />
          Laboratoire Volumétrie - Analyste
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Analyses volumétriques par titration
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Prêt pour Analyse
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.ready_for_analysis}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🧪
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    En Cours
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.in_progress}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ⏳
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Terminé Aujourd'hui
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.completed_today}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ✅
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.error.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Échantillons Urgents
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.urgent_samples}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🚨
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp color="primary" />
                Métriques de Performance
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Titrations Totales</Typography>
                  <Typography variant="body2" fontWeight="bold">{stats.total_titrations}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Précision Moyenne (%)</Typography>
                  <Typography variant="body2" fontWeight="bold">{stats.average_precision}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assessment color="success" />
                Méthodes Disponibles
              </Typography>
              <List dense>
                {volumetrieMethods.map((method, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Opacity fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={method.name}
                      secondary={`Titrant: ${method.titrant} | Indicateur: ${method.indicator}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Échantillons Volumétrie ({samples.length})
            </Typography>
            <Button
              startIcon={<Refresh />}
              onClick={loadVolumetrieAnalysisData}
              variant="outlined"
            >
              Actualiser
            </Button>
          </Box>

          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID Échantillon</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Méthodes</TableCell>
                  <TableCell>Progrès</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {samples.map((sample) => (
                  <TableRow key={sample.id} hover>
                    <TableCell>{sample.sample_id}</TableCell>
                    <TableCell>{sample.client?.company_name || sample.client_name || 'N/A'}</TableCell>
                    <TableCell>{sample.type_echantillon}</TableCell>
                    <TableCell>
                      <Chip
                        label={sample.statut}
                        size="small"
                        color={sample.statut.includes('COMPLETED') ? 'success' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sample.priority || sample.priorite || 'NORMAL'}
                        size="small"
                        color={getPriorityColor(sample.priority || sample.priorite || 'NORMAL')}
                      />
                    </TableCell>
                    <TableCell>
                      {sample.selected_laboratory_methods?.filter((m: string) => m.includes('VOLUMETRIE')).map((method: string) => (
                        <Chip key={method} label={method} size="small" sx={{ mr: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={sample.progress_percentage || 0}
                          sx={{ width: 60 }}
                        />
                        <Typography variant="body2">
                          {sample.progress_percentage || 0}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        startIcon={<Science />}
                        size="small"
                        onClick={() => handleStartAnalysis(sample)}
                        disabled={sample.statut !== 'LABORATOIRE_VOLUMETRIE_ANALYSE'}
                      >
                        Analyser
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      <Dialog open={analysisDialog} onClose={() => setAnalysisDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalDrink sx={{ color: '#059669' }} />
            Analyse Volumétrie - {selectedSample?.sample_id}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
            <Tab label="Conditions Titration" />
            <Tab label="Résultats d'Analyse" />
            <Tab label="Contrôle Qualité" />
          </Tabs>

          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Volume Burette (mL)"
                  type="number"
                  value={analysisData.instrument_conditions.burette_volume}
                  onChange={(e) => setAnalysisData(prev => ({
                    ...prev,
                    instrument_conditions: { ...prev.instrument_conditions, burette_volume: parseInt(e.target.value) }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Précision Burette (mL)"
                  type="number"
                  value={analysisData.instrument_conditions.burette_precision}
                  onChange={(e) => setAnalysisData(prev => ({
                    ...prev,
                    instrument_conditions: { ...prev.instrument_conditions, burette_precision: parseFloat(e.target.value) }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Température (°C)"
                  type="number"
                  value={analysisData.instrument_conditions.temperature}
                  onChange={(e) => setAnalysisData(prev => ({
                    ...prev,
                    instrument_conditions: { ...prev.instrument_conditions, temperature: parseInt(e.target.value) }
                  }))}
                  sx={{ mb: 2 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Notes d'Analyse"
                  multiline
                  rows={8}
                  value={analysisData.notes}
                  onChange={(e) => setAnalysisData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Résultats de Titration</Typography>
                <Button startIcon={<Add />} onClick={addAnalysisResult}>
                  Ajouter Résultat
                </Button>
              </Box>

              {analysisData.analysis_results.map((result, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Analyte"
                          value={result.analyte}
                          onChange={(e) => updateAnalysisResult(index, 'analyte', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Méthode</InputLabel>
                          <Select
                            value={result.method}
                            onChange={(e) => updateAnalysisResult(index, 'method', e.target.value)}
                          >
                            {volumetrieMethods.map((method) => (
                              <MenuItem key={method.method} value={method.method}>
                                {method.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Volume Initial (mL)"
                          type="number"
                          value={result.initial_volume}
                          onChange={(e) => updateAnalysisResult(index, 'initial_volume', parseFloat(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Volume Final (mL)"
                          type="number"
                          value={result.final_volume}
                          onChange={(e) => updateAnalysisResult(index, 'final_volume', parseFloat(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Volume Consommé (mL)"
                          value={result.volume_consumed.toFixed(2)}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Concentration (mol/L)"
                          type="number"
                          value={result.concentration_mol_l}
                          onChange={(e) => updateAnalysisResult(index, 'concentration_mol_l', parseFloat(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Concentration (%)"
                          type="number"
                          value={result.concentration_percent}
                          onChange={(e) => updateAnalysisResult(index, 'concentration_percent', parseFloat(e.target.value))}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {activeTab === 2 && (
            <Alert severity="info">
              Contrôles qualité pour l'analyse volumétrique - Standards et blancs.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialog(false)}>Annuler</Button>
          <Button
            onClick={handleCompleteAnalysis}
            variant="contained"
            startIcon={<Save />}
            disabled={analysisData.analysis_results.length === 0}
          >
            Finaliser Analyse
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VolumetrieAnalystDashboard; 